// @ts-nocheck
import { flatten, isEmpty, isEqual, omit, pick, uniqueId } from 'lodash';
import moment from 'moment-timezone';
import React, { createContext, useContext, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';

import { formatTimeFrame, getNewTimeFrame } from '@/helpers/reporting.helpers';
import { validEmailAddress } from '@/services/helpers';
import { getTimeZone } from '@/store/selectors/reportingAdvancedFilter.selectors';
import { getCurrentUserEmail } from '@/store/selectors/user.selectors';
import { useScheduleReportRequest } from '@/ts-modules/reporting/ReportDownload/hooks/useScheduleReportRequest';
import { getInitialTabOptions } from './helpers/ReportingDownload.helper';
import { useReportDownloadRequest } from './hooks/useReportDownloadRequest';

import type { Moment } from 'moment-timezone';
import type { PropsWithChildren } from 'react';
import type {
  Columns,
  DataByType,
  ExportFileFormat,
  OtherOptions,
  ReportConfigInitialData,
  ScheduleConfig,
  TabOptions
} from './types';

const BASE_DATE_FORMAT = 'YYYY-MM-DD';
const DATE_FORMAT = 'YYYY-MM-DD HH:mm:ss';

type State = {
  hasChanges: boolean;
  isDisabled: boolean;
  isSchedule: boolean;
  isScheduleValid: boolean;
  name: string;
  scheduleConfig: ScheduleConfig;
  tabsOptions: Record<string, TabOptions>;
  type: ExportFileFormat;
};

type BaseState = Pick<State, 'name' | 'scheduleConfig' | 'tabsOptions' | 'type'>;

type ContextValue = {
  createTab: () => void;
  deleteTab: (tabId: string) => void;
  downloadReport: () => void;
  editTabName: (name: string, tabId: string) => void;
  getOtherOptions: (tabId: string) => OtherOptions;
  getTabOptions: (tabName: string) => TabOptions;
  initialDateRange: {
    endDate: Moment;
    startDate: Moment;
  };
  isDisabled: boolean;
  isLoading: boolean;
  isLoadingSchedule: boolean;
  isMultiCampaigns: boolean;
  isSchedule: boolean;
  isScheduleUpdate: boolean;
  isScheduleValid: boolean;
  name: string;
  reset: () => void;
  saveAsNewSchedule: (successCallback?: CallableFunction) => void;
  saveChangeSchedule: (successCallback?: CallableFunction) => void;
  scheduleConfig: ScheduleConfig;
  setCampaigns: (campaignsIds: string[], tabId: string, isCustomLines?: boolean) => void;
  setColumnsByType: (tabId: string, statsType: keyof DataByType) => void;
  setColumnsOptions: (tabId: string, cols: Columns[]) => void;
  setDates: (tabId: string, start: Date, end: Date) => void;
  setDecimalsType: (decimalsType: string, tabId: string) => void;
  setFilters: (filters: string[], groups: string[], tabId: string) => void;
  setGroupsFromReporting: (tabId: string, dataType: keyof DataByType) => void;
  setName: (name: string) => void;
  setOtherOptions: (tabId: string, values: OtherOptions) => void;
  setScheduleConfig: (scheduleConfig: ScheduleConfig | null) => void;
  setTabOptions: (tabOptions: Record<string, TabOptions>) => void;
  setTimeFrame: (timeFrame: string, tabId: string) => void;
  setType: (type: ExportFileFormat) => void;
  tabsIds: string[];
  tabsOptions: TabOptions[];
  type: ExportFileFormat;
  userId: string;
};

const checkScheduleConfigIsValid = (scheduleConfig: ScheduleConfig): boolean => {
  const isPeriodicityValid = () => {
    if (isEmpty(scheduleConfig.hours) || !scheduleConfig.timeFrame.end || !scheduleConfig.timeFrame.start) {
      return false;
    }
    const endMoment = moment.tz(
      scheduleConfig.timeFrame.end!.toISOString()!,
      BASE_DATE_FORMAT,
      scheduleConfig.timezone
    );
    endMoment.set('hours', Number(scheduleConfig.hours[scheduleConfig.hours.length - 1]));
    endMoment.set('minutes', 0);
    endMoment.set('seconds', 0);
    if (endMoment.isBefore(moment())) {
      return false;
    }

    switch (scheduleConfig.periodicity) {
      case 'monthly':
        return scheduleConfig.monthDay !== undefined;
      case 'weekly':
        return scheduleConfig.weekDay !== undefined;
      default:
        return true;
    }
  };
  return (
    Boolean(scheduleConfig.emails?.length) &&
    flatten(scheduleConfig.emails.map(i => i.split(','))).every(email => validEmailAddress(email)) &&
    scheduleConfig.timeFrame.start !== undefined &&
    scheduleConfig.timeFrame.end !== undefined &&
    isPeriodicityValid()
  );
};

const checkScheduleConfigHasChange = (originalConfig: BaseState, currentConfig: BaseState): boolean =>
  !isEqual(
    {
      ...originalConfig,
      tabsOptions: Object.values(originalConfig.tabsOptions)
    },
    {
      ...currentConfig,
      tabsOptions: Object.values(currentConfig.tabsOptions)
    }
  );

const ReportDownloadSection = createContext<ContextValue | null>(null);

type ReportDownloadSectionProviderProps = {
  campaignIds: string[];
  dataByType: DataByType;
  defaultEnd: Date;
  defaultStart: Date;
  hasAttribution?: boolean;
  initialData?: ReportConfigInitialData;
  statsGroups: Record<keyof DataByType, string[]>;
  userId: string;
};

const generateTabId = (): string => {
  return uniqueId('Tab');
};

const getInitialState = (
  userId: string,
  campaigns: string[],
  start: Date,
  end: Date,
  timezone: string,
  dataByType: DataByType,
  userEmail: string,
  initialData?: ReportConfigInitialData
): State => {
  const scheduleConfig: ScheduleConfig = {
    ...pick(initialData?.scheduleConfig, ['monthDay', 'weekDay']),
    emails: initialData?.scheduleConfig?.emails || [userEmail],
    enable: initialData?.scheduleConfig?.enable || true,
    hours: initialData?.scheduleConfig?.hours || [],
    periodicity: initialData?.scheduleConfig?.periodicity || 'once',
    timeFrame: {
      end: initialData?.scheduleConfig?.timeFrame.end
        ? moment.utc(initialData?.scheduleConfig?.timeFrame.end, BASE_DATE_FORMAT)
        : null,
      start: initialData?.scheduleConfig?.timeFrame.start
        ? moment.utc(initialData?.scheduleConfig?.timeFrame.start, BASE_DATE_FORMAT)
        : null
    },
    timezone: initialData?.scheduleConfig?.timezone || timezone
  };

  return {
    hasChanges: false,
    isDisabled: false,
    isSchedule: !!initialData?.scheduleConfig || false,
    isScheduleValid: checkScheduleConfigIsValid(scheduleConfig) || false,
    name: initialData?.name || `Report Download ${moment().format(DATE_FORMAT)}`,
    scheduleConfig,
    tabsOptions: initialData?.tabsOptions.reduce(
      (prev, tabOption, currentIndex) => ({
        ...prev,
        [generateTabId()]: getInitialTabOptions(
          userId,
          campaigns,
          start,
          end,
          timezone,
          dataByType,
          currentIndex,
          tabOption
        )
      }),
      {}
    ) || {
      [generateTabId()]: getInitialTabOptions(userId, campaigns, start, end, timezone, dataByType, 1)
    },
    type: initialData?.type || 'excel'
  };
};

function ReportDownloadSectionProvider({
  campaignIds,
  children,
  dataByType,
  defaultEnd,
  defaultStart,
  hasAttribution = false,
  initialData,
  statsGroups,
  userId
}: PropsWithChildren<ReportDownloadSectionProviderProps>): React.ReactElement {
  const timezone = useSelector(getTimeZone);
  const userEmail = useSelector(getCurrentUserEmail);
  const initialDateRange = {
    endDate: moment.utc(defaultEnd).startOf('day'),
    startDate: moment.utc(defaultStart).startOf('day')
  };

  const [state, setState] = useState<State>(
    getInitialState(userId, campaignIds, defaultStart, defaultEnd, timezone, dataByType, userEmail, initialData)
  );

  if (state.isSchedule) {
    const originalConfig = getInitialState(
      userId,
      campaignIds,
      defaultStart,
      defaultEnd,
      timezone,
      dataByType,
      userEmail,
      initialData
    );
    const hasChanged = checkScheduleConfigHasChange(
      pick(originalConfig, ['name', 'scheduleConfig', 'tabsOptions', 'type']),
      pick(state, ['name', 'scheduleConfig', 'tabsOptions', 'type'])
    );
    if (state.hasChanges !== hasChanged) {
      setState(prevState => ({
        ...prevState,
        hasChanges: hasChanged
      }));
    }
  }

  const tabsIds = Object.keys(state.tabsOptions);

  const getTabOptions = (id: string): TabOptions => {
    const options = state.tabsOptions[id];
    if (!options) {
      throw new Error(`ReportDownloadSectionProvider: Tab id: ${id} does not exist`);
    }
    return options;
  };

  const setTabOptions = (tabOptions: Record<string, TabOptions>) => {
    setState(prev => ({
      ...prev,
      tabsOptions: tabOptions
    }));
  };

  const setScheduleConfig = (scheduleConfig: ScheduleConfig | null) => {
    setState(prev => ({
      ...prev,
      isSchedule: !!scheduleConfig,
      isScheduleValid: scheduleConfig ? checkScheduleConfigIsValid(scheduleConfig) : false,
      ...(scheduleConfig && { scheduleConfig })
    }));
  };

  const createTab = (): void => {
    const newTab = getInitialTabOptions(
      userId,
      campaignIds,
      defaultStart,
      defaultEnd,
      timezone,
      dataByType,
      tabsIds.length + 1
    );
    setState(prev => ({
      ...prev,
      tabsOptions: {
        ...prev.tabsOptions,
        [generateTabId()]: newTab
      }
    }));
  };

  const editTabName = (tabId: string, name: string): void => {
    if (!name) {
      return;
    }
    const newTabsOptions = structuredClone(state.tabsOptions);
    newTabsOptions[tabId].sheetName = name;
    setState(prev => ({
      ...prev,
      tabsOptions: newTabsOptions
    }));
  };

  const deleteTab = (tabId: string): void => {
    setState(prev => ({
      ...prev,
      tabsOptions: omit(state.tabsOptions, [tabId])
    }));
  };

  const setName = (name: string) =>
    setState(prev => ({
      ...prev,
      name
    }));

  const setType = (type: ExportFileFormat) =>
    setState(prev => ({
      ...prev,
      type
    }));

  const setFilters = (filters: string[], groups: string[], tabId: string) => {
    const newTabsOptions = structuredClone(state.tabsOptions);
    newTabsOptions[tabId].options.filters = filters;
    newTabsOptions[tabId].options.groups = groups;
    setState(prev => ({
      ...prev,
      tabsOptions: newTabsOptions
    }));
  };

  const setDecimalsType = (decimalsType: string, tabId: string) => {
    const newTabsOptions = structuredClone(state.tabsOptions);
    newTabsOptions[tabId].options.decimalsType = decimalsType;
    setState(prev => ({
      ...prev,
      tabsOptions: newTabsOptions
    }));
  };

  const setDates = (tabId: string, start: Date, end: Date) => {
    const newTabsOptions = structuredClone(state.tabsOptions);
    const { end: endFormatted, start: startFormatted } = formatTimeFrame(
      {
        end,
        start
      },
      timezone
    );
    newTabsOptions[tabId].start = startFormatted;
    newTabsOptions[tabId].end = endFormatted;
    setState(prev => ({
      ...prev,
      tabsOptions: newTabsOptions
    }));
  };

  const setTimeFrame = (timeFrame: string, tabId: string) => {
    const newTabsOptions = structuredClone(state.tabsOptions);
    newTabsOptions[tabId].timeFrame = timeFrame;
    if (timeFrame !== 'customDates') {
      const { beginDate, endDate } = getNewTimeFrame(timeFrame, {
        beginDate: initialDateRange.startDate.format('YYYY-MM-DD'),
        endDate: initialDateRange.endDate.format('YYYY-MM-DD')
      });
      const timeFrameFormatted = formatTimeFrame(
        {
          end: endDate,
          start: beginDate
        },
        timezone
      );
      newTabsOptions[tabId].start = timeFrameFormatted.start;
      newTabsOptions[tabId].end = timeFrameFormatted.end;
    }
    setState(prev => ({
      ...prev,
      tabsOptions: newTabsOptions
    }));
  };

  const setCampaigns = (newCampaignsIds: string[], tabId: string, isCustomLines?: boolean): void => {
    const newTabsOptions = structuredClone(state.tabsOptions);
    newTabsOptions[tabId].campaigns = newCampaignsIds;
    if (typeof isCustomLines === 'boolean') {
      newTabsOptions[tabId].isCustomSelectedCampaings = isCustomLines;
    }
    const hasSelectedCampaigns = Object.values(newTabsOptions).every(tab => tab.campaigns.length);
    setState(prev => ({
      ...prev,
      isDisabled: !hasSelectedCampaigns,
      tabsOptions: newTabsOptions
    }));
  };

  const setColumnsOptions = (tabId: string, cols: Columns[]) => {
    const newTabsOptions = structuredClone(state.tabsOptions);
    newTabsOptions[tabId].columns = cols;
    setState(prev => ({
      ...prev,
      tabsOptions: newTabsOptions
    }));
  };

  const getOtherOptions = (tabId: string): OtherOptions => {
    const tabOption = state.tabsOptions[tabId];
    return {
      addressInfo: tabOption.options.addressInfo,
      coordinatesInfo: tabOption.options.coordinatesInfo,
      decimalsType: tabOption.options.decimalsType
    };
  };

  const setOtherOptions = (tabId: string, values: OtherOptions) => {
    const newTabsOptions = structuredClone(state.tabsOptions);
    newTabsOptions[tabId].options.coordinatesInfo = values.coordinatesInfo;
    newTabsOptions[tabId].options.addressInfo = values.addressInfo;
    newTabsOptions[tabId].options.decimalsType = values.decimalsType;

    setState(prev => ({
      ...prev,
      tabsOptions: newTabsOptions
    }));
  };

  const setGroupsFromReporting = (tabId: string, dataType: keyof DataByType) => {
    const newTabsOptions = structuredClone(state.tabsOptions);

    if (statsGroups[dataType].length && !isEqual(newTabsOptions[tabId].options.groups, statsGroups[dataType])) {
      newTabsOptions[tabId].options.groups = statsGroups[dataType];
      setState(prev => ({
        ...prev,
        tabsOptions: newTabsOptions
      }));
    }
  };

  const setColumnsByType = (tabId: string, statsType: keyof DataByType) => {
    const newTabsOptions = structuredClone(state.tabsOptions);
    newTabsOptions[tabId].sheetType = statsType;
    newTabsOptions[tabId].columns = dataByType[statsType];
    newTabsOptions[tabId].options.groups = ['day'];

    setState(prev => ({
      ...prev,
      tabsOptions: newTabsOptions
    }));
  };

  const reset = () => {
    setState(
      getInitialState(userId, campaignIds, defaultStart, defaultEnd, timezone, dataByType, userEmail, initialData)
    );
  };

  const { downloadReport, isLoading } = useReportDownloadRequest(
    state.name,
    Object.values(state.tabsOptions),
    state.type
  );

  const { isLoading: isLoadingSchedule, scheduleReport } = useScheduleReportRequest(
    state.name,
    Object.values(state.tabsOptions),
    state.type,
    state.scheduleConfig
  );

  const saveAsNewSchedule = (successCallback?: CallableFunction) => scheduleReport(successCallback);
  const saveChangeSchedule = (successCallback?: CallableFunction) =>
    scheduleReport(successCallback, initialData?.scheduleConfig);

  const value: ContextValue = useMemo(
    () => ({
      createTab,
      deleteTab,
      downloadReport,
      editTabName,
      getOtherOptions,
      getTabOptions,
      hasAttribution,
      initialDateRange,
      isDisabled: state.isDisabled,
      isLoading,
      isLoadingSchedule,
      isMultiCampaigns: campaignIds.length > 1,
      isSchedule: state.isSchedule,
      isScheduleUpdate: Boolean(initialData?.scheduleConfig?._id),
      isScheduleValid: state.isScheduleValid && state.hasChanges,
      name: state.name,
      reset,
      saveAsNewSchedule,
      saveChangeSchedule,
      scheduleConfig: state.scheduleConfig,
      setCampaigns,
      setColumnsByType,
      setColumnsOptions,
      setDates,
      setDecimalsType,
      setFilters,
      setGroupsFromReporting,
      setName,
      setOtherOptions,
      setScheduleConfig,
      setTabOptions,
      setTimeFrame,
      setType,
      tabsIds,
      tabsOptions: Object.values(state.tabsOptions),
      type: state.type,
      userId
    }),
    [state, isLoading]
  );

  return <ReportDownloadSection.Provider value={value}>{children}</ReportDownloadSection.Provider>;
}

export const useReportDownloadSectionContext = (): ContextValue => {
  const context = useContext(ReportDownloadSection);
  if (!context) {
    throw new Error('useReportDownloadSectionProvider needs a ReportDownloadSectionProvider');
  }
  return context;
};

export default ReportDownloadSectionProvider;
